// =============================================
// Draft Generator Service
// v3.0: Block-by-block writing with Writing Profile + sliding window
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { parseLLMJSON, getLLMProvider, getStageConfig } from "@/services/llm";
import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import type { OutlineSection, OutlineH3, Outline, SectionDistribution } from "./outlineGenerator";
import type { KnowledgeGraph, KGEntity, KGFact, KGIdeation, KGRelationship } from "./knowledgeGraph";
import type { ThematicWritingProfile } from "@/types/thematicMaps";
import { logStage, logLlm, logProfile, logIntegrity, logPublic, logDebug } from "./contentLogger";
import { compilePrompt, compilePromptSafe, validateNoMetaOutput } from "./promptCompiler";
import { hashNodeId, pickFromList } from "@/lib/utils";

// KB 3.2 Query Frame Match: hooks dozwolone per document_role dla intro_generation v2.
// Definitional opening dla definition/glossary, scenariusz dla how_to/troubleshooting,
// kontrast dla comparison, liczba dla data-heavy roli (commercial/troubleshooting).
const ALLOWED_HOOKS_PER_ROLE: Record<string, string[]> = {
  definition: ["BLUF", "KONTRAST", "LICZBA"],
  comparison: ["BLUF", "KONTRAST", "SCENA"],
  how_to: ["SCENA", "LICZBA", "BLUF"],
  troubleshooting: ["SCENA", "KONTRAST", "LICZBA"],
  history: ["BLUF", "LICZBA"],
  guide: ["BLUF", "KONTRAST", "SCENA"],
  glossary: ["BLUF", "KONTRAST"],
  commercial: ["LICZBA", "KONTRAST", "BLUF"],
};

// Hook → letter mapping dla intro_generation v2 sub-pattern reference.
// Prompt v2 ma sekcje "### A1/A2/A3" (Hook A — BLUF), "### B1/B2/B3" (Hook B — SCENA), etc.
// charAt(0) hookType produkowal wrong letters (BLUF → 'B' zamiast 'A') — explicit mapping fix.
const HOOK_TO_LETTER: Record<string, string> = {
  BLUF: "A",
  SCENA: "B",
  LICZBA: "C",
  KONTRAST: "D",
};

// =============================================
// Types
// =============================================

interface GenerationBlock {
  sectionOrder: number;
  h2Header: string;
  sectionType: "full" | "context";
  h3s: OutlineH3[];
  passageFormat: string;
  entities: KGEntity[];
  facts: KGFact[];
  ideations: KGIdeation[];
  relationships: KGRelationship[];
  // SRO: source_area z outline (zawiera tag kategorii np. "[BOUNDARIES] ...").
  // Uzywany przez F1b semantic EAV fallback w draft — tag → attribute pattern map.
  sourceArea?: string;
}

// =============================================
// F1b: SRO tag → EAV attribute patterns mapping
// Uzywane gdy distribution.entity_ids dla sekcji jest pusty (BOUNDARIES/innym
// kategoriom czesto brakuje encji w KG — KG Assembler nie ekstraktuje "anti-patterns"
// z SERP content). Zamiast round-robin fallback → semantic match po attribute_name.
// =============================================

const SRO_TAG_TO_EAV_PATTERNS: Record<string, RegExp> = {
  DIRECT_ANSWER: /^(definicja|czym_jest|operational|nazwa_|definicja_operacyjna)/i,
  ATTRIBUTES: /^(roznica_od|cechy|atrybuty|charakterystyki|wyroznia)/i,
  COMPONENTS: /^(skladniki|etapy|komponenty|liczba_etapow|warstwy)/i,
  PROVENANCE: /^(tworca|rok|historia|kto_wymyslil|pierwsza_)/i,
  EVIDENCE: /^(kanoniczne_przyklady|case_studies|przyklady_firm|przypadki)/i,
  BOUNDARIES: /^(kiedy_szkodzi|granice|limity|kiedy_nie|ryzyko)/i,
  STEPS: /^(kroki|procedura|workflow|etapy_procesu)/i,
  TOOLS: /^(narzedzia|toolstack|platformy|stack)/i,
  COMMON_MISTAKES: /^(typowe_bledy|pulapki|bledy|glowne_bledy)/i,
  VERIFICATION: /^(benchmark|metryki_sukcesu|kluczowe_metryki|jak_sprawdzic)/i,
  SYMPTOMS: /^(objaw|symptom|jak_rozpoznac)/i,
  CAUSES: /^(przyczyna|dlaczego|root_cause)/i,
  SOLUTIONS: /^(rozwiazanie|fix|jak_naprawic)/i,
  PREVENTION: /^(zapobieganie|jak_unikac|prewencja)/i,
  ORIGINS: /^(tworca|rok_powstania|pierwsze_uzycie)/i,
  EVOLUTION: /^(kamienie_milowe|ewolucja|kluczowe_momenty)/i,
  CURRENT_STATE: /^(stan_obecny|aktualna_wersja|uzywane_dzis)/i,
  LEGACY: /^(wplyw_na|dziedzictwo|spuscizna)/i,
  PRODUCT_SPECS: /^(cechy|funkcje|co_zawiera)/i,
  PRICING: /^(cena|plany|koszty|wariant)/i,
  BUY_OPTIONS: /^(gdzie_kupic|sklepy|dystrybutor)/i,
  REVIEWS: /^(opinie|recenzje|rating)/i,
  // === KROK 3 glossary (+4) ===
  // TERM_DEFINITION bez `synonim` — rozłączone od SYNONYMS_AND_VARIANTS (user FIX 1).
  TERM_DEFINITION: /^(oznacza|znaczy|to_jest|rola)/i,
  SYNONYMS_AND_VARIANTS: /^(synonim|alias|nazywane|wariant_nazwy)/i,
  USAGE_CONTEXT: /^(zastosowanie|stosowane|branza|kontekst|uzywa|uzycie|obszar)/i,
  COMMON_CONFUSIONS: /^(roznica_od|roznica_vs|mylone_z|nie_to_samo_co)/i,
  // === KROK 3 guide (+5) ===
  OVERVIEW: /^(cel|rola|funkcja|glowny_obszar)/i,
  PREREQUISITES: /^(wymagania|warunek|warunki|minimum|prerekwizyty|wiedza_specjalistyczna)/i,
  CORE_CONCEPTS: /^(kluczow|podstawow|fundament|model|koncepcj)/i,
  IMPLEMENTATION_PATH: /^(etap|wdrozenie|standard_implementacj|kroki|sciezka|faza)/i,
  // 'bł' łapie polskie diakrytyki: błąd_I_rodzaju, błędy_interpretacji (FIX 2)
  COMMON_PITFALLS: /^(typowe_bledy|typowy_bl|glowne_bledy|blad|bled|bł|ryzyko|ograniczenia|pulapki)/i,
};

/** Wydobywa tag kategorii SRO z source_area (np. "[BOUNDARIES] ..." → "BOUNDARIES") */
function extractSroTag(sourceArea: string | undefined): string | null {
  if (!sourceArea) return null;
  const m = sourceArea.match(/\[([A-Z_]+)\]/);
  return m ? m[1] : null;
}

// =============================================
// H3 passage character per SRO Passage Tag (SRO 4.2 — extractable claim, named entity, verifiable detail)
// =============================================
//
// Charakter passage per SRO tag wynika z faktycznej SRO 4.2 (Passage Engineering):
// - extractable claim (nie topic label) — H3 cytowalna jako standalone passage
// - entity explicitly named (no pronouns) — primary entity w 1-2 pierwszych zdaniach
// - min 1 verifiable detail per H3 — number, comparison, mechanism, condition, date, author
// - format-frame match per query frame (SRO 3.2) — Procedural/Definitional/Comparative/Causal
//
// Dlugosc H3 wynika z TRESCI passage, nie sztywnej liczby slow. Liczby 80-150/150-280/200-380
// (poprzednia mapa SRO_TAG_TO_H3_LENGTH) byly arbitralnym artefaktem PBN-SD, nie SRO.
// Anti-symmetry rule egzekwowana przez post-processing guard validateH3Variance() — naturalna
// roznica dlugosci wynika z roznego charakteru passage, nie z hardcoded zakresow.

const SRO_TAG_TO_PASSAGE_CHARACTER: Record<string, string> = {
  DIRECT_ANSWER: "extractable answer w 1-2 zdaniach definiujacych + 1-2 zdania kontekstu (kandydat AI Overview / Featured Snippet, SRO 4.1)",
  TERM_DEFINITION: "definicja terminu jednym zdaniem + disambiguating qualifier + 1 verifiable detail (SRO 4.2)",
  BOUNDARIES: "passage co-jest-IN-co-jest-OUT scope: 2-3 example IN, 2-3 example OUT, kryterium granicy",
  VERIFICATION: "kryterium weryfikacji: pytanie testowe + odpowiedz jak sprawdzic + 1 edge case",
  ATTRIBUTES: "passage z 2-3 verifiable attribute values per H3 (liczby/jednostki/zakresy z EAV grounding)",
  COMPONENTS: "wymien komponenty + role kazdego + relacje miedzy nimi (1 zdanie per komponent)",
  USAGE_CONTEXT: "passage scenarios uzycia: kiedy/gdzie/dla-kogo + warunki",
  SYMPTOMS: "lista obserwowalnych sygnalow + jak rozpoznac w praktyce + 1 konkretny przyklad per sygnal",
  EVIDENCE: "passage z dowodami: liczby, badania, casy, daty, autorzy (min 1 verifiable per zdanie - SRO 4.2)",
  CRITERIA: "kryteria oceny: lista warunkow + jak sprawdzic + co dyskwalifikuje",
  STEPS: "rozbudowana procedura krok-po-kroku: numbered list, action verb per krok, prerequisite, expected outcome (Procedural query frame - SRO 3.2)",
  SOLUTIONS: "rozwiazania problemu: wymien podejscia + kiedy ktore stosowac + tradeoffs per podejscie",
  SIDE_BY_SIDE: "tabela porownawcza 2+ kolumn + komentarz kiedy ktora wybrac (Comparative query frame - SRO 3.2)",
  COMMON_MISTAKES: "lista bledow + dlaczego blad + jak uniknac + przyklad per blad",
  USE_CASES: "scenariusze uzycia: situation + zastosowanie + outcome",
  PREVENTION: "early warning signs + actions + redukcja ryzyka (1 zdanie per element)",
  CAUSES: "analiza przyczyn: root causes + mechanizmy + jak sie kumuluja (Causal query frame - SRO 3.2)",
};

function buildPassageGuidance(sourceArea: string | undefined): string {
  const tag = extractSroTag(sourceArea);
  if (!tag) {
    return "Passage musi spelniac SRO 4.2: extractable claim (nie topic label), entity explicitly named (no pronouns), min 1 verifiable detail (number/comparison/mechanism/condition). Dlugosc wynika z tresci.";
  }
  const character = SRO_TAG_TO_PASSAGE_CHARACTER[tag] || "extractable passage claim z named entity + verifiable detail";
  // Why: literalny `[${tag}]` w treści promptu (poprzednie "Sekcja H2 z tagiem [DIRECT_ANSWER]")
  // → gpt-5.4 hallucynowal `[DIRECT_ANSWER]` jako suffix w generowanym <h2>.
  // Tag opisujemy słownie ("Kategoria passage: TAG"), per-tag rozróżnienie zachowane przez `character`.
  return `Kategoria passage tej sekcji: ${tag}. Kazdy H3 = ${character}. SRO 4.2: extractable claim, named entity, min 1 verifiable detail per H3.\n\nKRYTYCZNE: naglowek H2 musi byc IDENTYCZNY z H2 podanym wyzej w sekcji "Naglowek H2" — NIE dodawaj zadnych suffixow w nawiasach kwadratowych ani markerow kategorii. Markery kategorii to internal classification, NIE czesc tekstu nagłówka.`;
}

// =============================================
// Anti-symmetry post-processing guard (Krok 3, 2026-04-26)
// =============================================
//
// Po wygenerowaniu bloku liczymy slowa per H3 (WYLACZNIE prose/listy/tabele PER H3,
// od </h3> do nastepnego <h3> lub <h2> lub konca bloku — NIE H2, NIE inne H3).
// Sprawdzamy KAZDA pare sasiednich H3 (1v2, 2v3, ...). Jesli JAKAKOLWIEK roznica < 30
// slow → retry (max 1) z explicit anti-symmetry warning w prompcie.
//
// Skip jesli blok ma <2 H3 (brak sasiadow do porownania). Po retry log integrity
// "anti_symmetry_retry_triggered" + finalne dlugosci H3 jako audit trail.

const H3_VARIANCE_THRESHOLD = 30; // slow roznicy miedzy sasiednimi H3

function countH3Words(blockHtml: string): number[] {
  // Match: <h3...>...</h3> + capture content AFTER </h3> until next <h3>, <h2>, or end.
  // BUG fix 2026-04-26: lookahead `(?=<h3[^>]|<h2[^>]|$)` wymagal non-`>` po `<h3`,
  // wiec nie matchowal `<h3>` (bez atrybutow) — pierwszy H3 zjadal caly content do konca,
  // h3_count zawsze =1. Fix: usunieto `[^>]` z lookahead — szuka po prostu `<h3` lub `<h2`.
  const counts: number[] = [];
  const regex = /<h3[^>]*>[\s\S]*?<\/h3>([\s\S]*?)(?=<h3|<h2|$)/gi;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(blockHtml)) !== null) {
    const rawContent = m[1] || "";
    // Strip HTML tags + entities + collapse whitespace
    const text = rawContent
      .replace(/<[^>]+>/g, " ")
      .replace(/&[a-z]+;/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
    const words = text.length > 0 ? text.split(/\s+/).filter(Boolean).length : 0;
    counts.push(words);
  }
  return counts;
}

interface H3Violation {
  idx1: number;
  idx2: number;
  w1: number;
  w2: number;
  diff: number;
}

function validateH3Variance(
  wordsPerH3: number[],
  threshold = H3_VARIANCE_THRESHOLD,
): { ok: boolean; violations: H3Violation[] } {
  const violations: H3Violation[] = [];
  for (let i = 0; i < wordsPerH3.length - 1; i++) {
    const diff = Math.abs(wordsPerH3[i] - wordsPerH3[i + 1]);
    if (diff < threshold) {
      violations.push({
        idx1: i,
        idx2: i + 1,
        w1: wordsPerH3[i],
        w2: wordsPerH3[i + 1],
        diff,
      });
    }
  }
  return { ok: violations.length === 0, violations };
}

// =============================================
// Heading trigger → passage format
// =============================================

// SRO tag → format mapping (HIGHEST priority — overrides header/role).
// Why: poprzedni getPassageInstructions(format) bral document_role i wkladal "Uzyj tabeli HTML"
// do wszystkich H2 dla document_role=comparison → 10 tabel w jednym artykule (5 H2 × 2 H3).
// Tabela powinna byc TYLKO dla SRO tagow ktore semantycznie wymagaja porownania ([SIDE_BY_SIDE]).
const SRO_TAG_TO_FORMAT: Record<string, string> = {
  // TABELA — tylko gdy semantycznie wymaga porownania
  SIDE_BY_SIDE: "comparison_table",

  // NUMBERED LIST — procedural
  STEPS: "step_by_step",
  IMPLEMENTATION_PATH: "step_by_step",

  // LISTA (ul) — wymienienie elementow
  CRITERIA: "criteria_list",
  COMPONENTS: "components_list",
  TOOLS: "tools_list",
  COMMON_MISTAKES: "mistakes_list",
  COMMON_PITFALLS: "mistakes_list",
  PREREQUISITES: "list",
  CORE_CONCEPTS: "list",
  SYNONYMS_AND_VARIANTS: "list",

  // PROSE — naturalne akapity, NIE tabela, NIE lista
  VERDICT: "prose",
  USE_CASES: "prose",
  USAGE_CONTEXT: "prose",
  SOLUTIONS: "prose",
  PREVENTION: "prose",
  VERIFICATION: "prose",
  BOUNDARIES: "prose",

  // DEFINICJA — extractable opening + elaboration
  DIRECT_ANSWER: "definition",
  TERM_DEFINITION: "definition",
  INTRODUCTION: "definition",
  OVERVIEW: "definition",
  PROVENANCE: "definition",

  // DATA PROSE — liczby/dane w prose, max 1 mini-tabela jesli sensowna
  EVIDENCE: "data_prose",
  ATTRIBUTES: "data_prose",
  PRODUCT_SPECS: "data_prose",
  PRICING: "data_prose",
  CURRENT_STATE: "data_prose",
  BUY_OPTIONS: "data_prose",

  // EXPLANATION — cause-effect prose
  CAUSES: "explanation",
  SYMPTOMS: "explanation",
  ORIGINS: "explanation",
  EVOLUTION: "explanation",
  LEGACY: "explanation",
  REVIEWS: "explanation",
  COMMON_CONFUSIONS: "explanation",
};

function detectPassageFormat(header: string, documentRole?: string, sourceArea?: string): string {
  // SRO tag z source_area = HIGHEST priority (overrides header/role)
  const sroTag = extractSroTag(sourceArea);
  if (sroTag && SRO_TAG_TO_FORMAT[sroTag]) {
    return SRO_TAG_TO_FORMAT[sroTag];
  }

  // Fallback 1: header-based detection
  const lower = header.toLowerCase();
  if (/co to|what is|czym jest|definicja/.test(lower)) return "definition";
  if (/jak |how to|krok po kroku|instrukcja/.test(lower)) return "step_by_step";
  if (/vs |porównanie|różnica|comparison/.test(lower)) return "comparison_table";
  if (/najlepsze|best|top |ranking/.test(lower)) return "ranked_list";
  if (/dlaczego|why|przyczyn/.test(lower)) return "explanation";
  if (/ile|koszt|cena|how much|price/.test(lower)) return "data_driven";

  // Fallback 2: document_role default
  if (documentRole) {
    const role = documentRole.toLowerCase();
    if (role === "definition" || role === "glossary") return "definition";
    if (role === "how_to" || role === "protocol" || role === "guide") return "step_by_step";
    if (role === "comparison") return "prose"; // changed from comparison_table — was forcing tabela in EVERY H2
    if (role === "troubleshooting") return "explanation";
    if (role === "commercial") return "data_prose";
  }
  return "standard";
}

function getPassageInstructions(format: string): string {
  switch (format) {
    case "definition":
      return "Zacznij od zwiezlej definicji (1-2 zdania) z named entity. Nastepnie rozwin kontekst, przyklady, zastosowania w prose. NIE uzywaj tabeli.";
    case "step_by_step":
      return "Uzyj numerowanej listy krokow (<ol>). Kazdy krok: action verb + prerequisite + expected outcome. NIE uzywaj tabeli.";
    case "comparison_table":
      return "Uzyj 1 tabeli HTML (<table>) porownawczej z min 2 kolumnami. Nastepnie krotki akapit z wnioskami. MAX 1 tabela w tej sekcji — NIE rob osobnej tabeli per H3.";
    case "criteria_list":
      return "Uzyj nieuporzadkowanej listy (<ul>) kryteriow z mini-opisem kazdego. Opcjonalnie 1 mini-tabela 2-kolumnowa (kryterium | znaczenie) jesli porzadkuje wymiary porownania. NIE rob wielu tabel.";
    case "components_list":
      return "Wymien komponenty jako liste (<ul>) lub prose z bold dla nazw. Kazdy element: rola + relacje z innymi. NIE uzywaj tabeli.";
    case "tools_list":
      return "Lista narzedzi (<ul>) z mini-opisem kazdego: nazwa + zastosowanie + alternatywa. NIE uzywaj tabeli.";
    case "mistakes_list":
      return "Lista typowych bledow (<ol> lub <ul>): blad → dlaczego to blad → jak uniknac → przyklad. NIE uzywaj tabeli.";
    case "list":
      return "Lista (<ul>) z mini-opisem kazdej pozycji. NIE uzywaj tabeli.";
    case "ranked_list":
      return "Numerowana lista (<ol>) z mini-opisem kazdej pozycji + kryterium wyboru. NIE uzywaj tabeli.";
    case "explanation":
      return "Struktura prose: teza → dowody → przyklad → wniosek. Buduj logiczny argument w akapitach. NIE uzywaj tabeli ani listy chyba ze tresc tego naturalnie wymaga.";
    case "data_prose":
      return "Prose z konkretnymi liczbami, datami, jednostkami, mechanizmami. Opcjonalnie max 1 mini-tabela jesli prawdziwie porownuje 2+ kolumny lub spec sheet. Inaczej prose.";
    case "prose":
      return "Naturalne akapity prose. NIE uzywaj tabeli ani listy. Jesli temat ma elementy do wymienienia — wplec w narracje z bold dla kluczowych pojec.";
    case "data_driven":
      return "Rozpocznij od konkretnych liczb/danych. Wizualizuj w tabeli TYLKO jesli porownuje 2+ kolumny. Inaczej prose.";
    default:
      return "Naturalny, ekspertowy styl. Uzywaj akapitow; listy i tabele tylko gdy tresc tego naturalnie wymaga (NIE wymuszaj).";
  }
}

// =============================================
// Writing Profile → system prompt
// =============================================

function buildWritingProfilePrompt(profile: ThematicWritingProfile | null): string {
  if (!profile) {
    return "Pisz jako ekspert SEO w naturalnym, przystępnym stylu po polsku.";
  }

  const parts: string[] = [];

  // Identity
  parts.push(`## Tożsamość autora\n${profile.author_identity || "Ekspert branżowy"}`);
  parts.push(`Misja: ${profile.mission || "Dostarczaj wartościowe treści"}`);
  parts.push(`Publiczność: ${profile.audience_primary || "ogólna"}`);

  // Style DNA
  parts.push(`\n## Styl pisania`);
  parts.push(`- Forma zwracania się: ${profile.voice_pronouns || "ty"}`);
  parts.push(`- Szczegółowość: ${profile.verbosity || "standard"}`);
  parts.push(`- Długość zdań: ${profile.sentence_length || "mixed"}`);
  parts.push(`- Analogie: ${profile.analogy_frequency || "moderate"}`);

  // Voice DNA v2 — opisowe cechy zamiast prescriptive fraz (anti-fingerprint)
  if ((profile as any).voice_characteristics?.length > 0) {
    parts.push(`- Cechy głosu autora: ${(profile as any).voice_characteristics.join("; ")}`);
  }
  if ((profile as any).content_personality) {
    parts.push(`- Osobowość treści: ${(profile as any).content_personality}`);
  }
  // NIE wstawiaj preferred_phrases ani banned_phrases — tworzą fingerprint
  if (profile.language_rules) {
    parts.push(`- Reguły językowe: ${profile.language_rules}`);
  }

  // Evidence
  parts.push(`\n## Dowody i twierdzenia`);
  parts.push(`- Styl dowodów: ${profile.evidence_style || "standard"}`);
  parts.push(`- Siła twierdzeń: ${profile.claim_strength || "moderate"}`);
  if (profile.disclaimer_template) {
    parts.push(`- Disclaimer: ${profile.disclaimer_template}`);
  }

  // Glossary
  if (profile.glossary_mode !== "none") {
    parts.push(`\n## Słownik`);
    parts.push(`- Tryb: ${profile.glossary_mode}`);
    if (profile.core_entities?.length > 0) {
      parts.push(`- Kluczowe terminy: ${profile.core_entities.join(", ")}`);
    }
  }

  return parts.join("\n");
}

// =============================================
// Block building
// =============================================

function buildBlocks(
  outline: Outline,
  kg: KnowledgeGraph,
  documentRole?: string,
): GenerationBlock[] {
  const blocks: GenerationBlock[] = [];

  for (const section of outline.sections) {
    if (section.type === "intro") continue; // Intro generated separately

    const dist = outline.distribution[section.order] || {
      entity_ids: [],
      fact_ids: [],
      ideation_ids: [],
      relationship_ids: [],
    };

    // Resolve IDs to actual KG items
    const entities = kg.entities.filter((e) => dist.entity_ids.includes(e.entity_id));
    const facts = kg.facts.filter((f) => dist.fact_ids.includes(f.fact_id));
    const ideations = kg.ideations.filter((i) => dist.ideation_ids.includes(i.ideation_id));
    const relationships = kg.entities_relationships.filter((r) =>
      dist.relationship_ids.includes(r.relationship_id),
    );

    blocks.push({
      sectionOrder: section.order,
      h2Header: section.header || "",
      sectionType: section.section_type,
      h3s: section.h3s || [],
      passageFormat: detectPassageFormat(section.header || "", documentRole, section.source_area || undefined),
      entities,
      facts,
      ideations,
      relationships,
      sourceArea: section.source_area || undefined,
    });
  }

  return blocks;
}

// =============================================
// Main function
// =============================================

/**
 * Generate article draft block-by-block with Writing Profile and sliding window context.
 */
export async function generateDraft(
  nodeId: string,
  mapId: string,
  outline: Outline,
  kg: KnowledgeGraph,
  seoTitle: string,
  language: string,
  onBlockProgress?: (blockIndex: number, totalBlocks: number) => void,
  ctxWritingProfile?: {
    author_identity: string; voice_pronouns: string; verbosity: string;
    sentence_length: string; preferred_phrases: string[]; banned_phrases: string[];
    claim_strength: string; evidence_style: string; analogy_frequency: string;
    analogy_domain: string; glossary_mode: string; language_rules: string;
  } | null,
  ctxInternalLinks?: Array<{ target_slug: string; target_title: string; relation_type: string }>,
  documentRole?: string,
  ctxEav?: Array<{ entity_name: string; attribute_name: string; value: string }>,
  nodeMeta?: { primary_entity?: string; canonical_question?: string },
  // STOP-C': options bag dla pillar flow (A1 z planu)
  // Backward compat: undefined → standard intro/section prompts, no extra vars.
  draftOptions?: {
    introPromptKey?: string;
    sectionPromptKey?: string;
    extraVars?: Record<string, string>;
  },
): Promise<{ draftHtml: string; costUsd: number; debug: Record<string, unknown> }> {
  let totalCost = 0;

  // 1. Load Writing Profile — from ctx if provided, else from DB
  let profile: ThematicWritingProfile | null = null;

  if (ctxWritingProfile !== undefined) {
    // ctx provided — use it directly (may be null = no profile)
    profile = ctxWritingProfile as ThematicWritingProfile | null;
  } else {
    // Fallback: fetch from DB (backward compat)
    const { data: map } = await (supabase as any)
      .from("thematic_maps")
      .select("active_profile_id")
      .eq("id", mapId)
      .single();

    if (map?.active_profile_id) {
      const { data: profileData } = await (supabase as any)
        .from("thematic_writing_profiles")
        .select("*")
        .eq("id", map.active_profile_id)
        .single();
      profile = profileData;
    }
  }

  const systemPromptBase = buildWritingProfilePrompt(profile);
  // Append OUTPUT GUARDS to system prompt
  const systemPrompt = systemPromptBase + `\n\n## BEZWZGLEDNE ZAKAZY OUTPUT
- NIGDY nie pros uzytkownika o brakujace dane
- NIGDY nie wyswietlaj zmiennych szablonowych (podwojne nawiasy klamrowe) ani placeholderow
- NIGDY nie komentuj brakow w kontekscie
- NIGDY nie pisz "brakuje danych", "podeslij", "uzupelnij"
- Zwracaj WYLACZNIE gotowy HTML`;
  logProfile("draft", profile);

  // 2. Load prompts from DB — separate for intro and sections.
  // STOP-C': pillar flow uses *_pillar prompts (via draftOptions.introPromptKey / sectionPromptKey).
  const introPromptDb = await loadPrompt((draftOptions?.introPromptKey ?? "intro_generation") as any);
  const sectionPromptDb = await loadPrompt((draftOptions?.sectionPromptKey ?? "section_generation") as any);
  const introStageConfig = getStageConfig("intro_generation" as any);
  const sectionStageConfig = getStageConfig("section_generation" as any);
  const stageConfig = getStageConfig("draft");
  const draftProvider = getLLMProvider(sectionPromptDb?.provider || sectionStageConfig?.provider || stageConfig.provider);
  const draftModel = sectionPromptDb?.modelId || sectionStageConfig?.model || stageConfig.model;

  // 3. Build blocks from outline + KG distribution
  const blocks = buildBlocks(outline, kg, documentRole);
  logStage("DRAFT", "START", { model: draftModel, chaining: draftModel.startsWith("gpt-5") || draftModel.startsWith("o1") || draftModel.startsWith("o3"), blocks: blocks.length, profile_name: profile?.author_identity || "(none)", document_role: documentRole || "(none)", prompt_loaded: !!sectionPromptDb });
  const perBlockDebug: Array<{ h2: string; chars: number; format: string; cost_usd: number; prompt: string; system_prompt: string; h3s: string[]; kg_entities: string[]; kg_facts: string[]; chaining: boolean; response_id?: string }> = [];

  // Detect if we should use Responses API (reasoning models with chaining)
  const useResponsesAPI = draftModel.startsWith("gpt-5") || draftModel.startsWith("o1") || draftModel.startsWith("o3");
  let lastResponseId: string | null = null;

  // LINKOWANIE WYLACZONE W BLOKACH (2026-08-23, architektura "najpierw pisz,
  // potem linkuj"): kazdy blok dostawal ta sama pelna liste z instrukcja
  // "wstaw 1-2 linki" -> 23% artykulow mialo duplikaty (270/1158). Linki wplata
  // teraz dedykowany etap Link Weaver (linkWeaver.ts) PO sklejeniu draftu —
  // jeden call na artykul, kazdy target max 1x, aplikacja deterministyczna.
  // ctxInternalLinks zostaje w sygnaturze (uzywa go orchestrator dla Weavera).
  void ctxInternalLinks;
  const internalLinksFormatted =
    "(brak — linkowaniem wewnetrznym zajmuje sie osobny etap; NIE wstawiaj zadnych linkow <a> w tej sekcji)";

  // 4. Generate intro
  const introSection = outline.sections.find((s) => s.type === "intro");
  const introDist = outline.distribution[0] || { entity_ids: [], fact_ids: [], ideation_ids: [], relationship_ids: [] };
  const introEntities = kg.entities
    .filter((e) => introDist.entity_ids.includes(e.entity_id))
    .map((e) => e.entity_name)
    .join(", ");

  // Build outline headers for intro context
  const outlineHeaders = outline.sections
    .filter((s) => s.type === "h2" && s.header)
    .map((s) => s.header)
    .join("\n");

  // KB 4.1: rotation 12 sub-patterns (4 hooks × 3) per nodeId — strukturalna unikalnosc cluster.
  // KB 3.2: ALLOWED_HOOKS_PER_ROLE filtruje hooks niezgodne z Query Frame dla danej roli.
  const safeRole = (typeof documentRole === "string" && documentRole.trim()) || "definition";
  const allowedHooks = ALLOWED_HOOKS_PER_ROLE[safeRole] ?? ["BLUF"];
  const introHookType = pickFromList(nodeId, allowedHooks, 0); // primary picker
  const subPatternIdx = (hashNodeId(nodeId) >>> 2) % 3; // 0/1/2 (independent dimension shift)
  // KB 4.1 fix: explicit HOOK_TO_LETTER mapping. charAt(0) dawalo BLUF→'B' (wrong), explicit map daje BLUF→'A' matching prompt v2 sekcje A1-A3/B1-B3/C1-C3/D1-D3.
  const introHookSubpattern = `${HOOK_TO_LETTER[introHookType] || "A"}${subPatternIdx + 1}`;

  // KB 1.1 source_context single-truth: nodeMeta.primary_entity (z runner.ctx.node.primary_entity_raw)
  // jest deterministyczny per node. Fallback chain: nodeMeta → kg.meta.main_entity (LLM-generated, drift risk)
  // → "" (legacy). KB 4.3 entity anchoring wymaga primary_entity w pierwszych 100 slowach intro.
  const introTemplateVars: Record<string, string> = {
    seo_title: seoTitle,
    language,
    canonical_question: nodeMeta?.canonical_question || seoTitle,
    primary_entity: nodeMeta?.primary_entity || kg.meta?.main_entity || "",
    document_role: documentRole || "standard",
    outline_headers: outlineHeaders || "",
    intro_entities: introEntities || "",
    main_question: introSection?.source_area || seoTitle,
    intro_hook_type: introHookType,
    intro_hook_subpattern: introHookSubpattern,
    // STOP-C': extra vars (np. cluster_member_entities, cluster_member_slugs, macro_statement dla pillar)
    ...(draftOptions?.extraVars ?? {}),
  };

  const INTRO_FALLBACK = `Napisz krótki wstęp (2-3 zdania) do artykułu "${seoTitle}" w języku ${language}.\n\nKluczowe encje: ${introEntities || "brak"}\nGłówne pytanie czytelnika: ${introSection?.source_area || seoTitle}\n\nZasady:\n- BEZ definicji\n- Odpowiedz na główne pytanie w 1-2 zdaniach\n- Zachęć do dalszego czytania\n- Format: HTML (<p> tags)\n- NIGDY nie wyswietlaj zmiennych szablonowych ani placeholderow`;

  const introPrompt = introPromptDb
    ? compilePromptSafe(introPromptDb.template, introTemplateVars, "intro_generation", INTRO_FALLBACK)
    : INTRO_FALLBACK;

  logDebug("DRAFT", `intro prompt_source=${introPromptDb ? "db" : "fallback"}, length=${introPrompt.length}`);
  logDebug("DRAFT", `Intro hook: ${introHookType}-${introHookSubpattern} (role=${safeRole})`);

  let introResponse;
  if (useResponsesAPI && draftProvider.callResponses) {
    logDebug("DRAFT", `Intro: model=${draftModel}, Responses API, chaining=NO`);
    introResponse = await draftProvider.callResponses(introPrompt, {
      model: draftModel,
      instructions: systemPrompt,
      reasoningEffort: "medium",
      step: "intro_generation", mapId, nodeId,
    });
    lastResponseId = introResponse.responseId || null;
    logDebug("DRAFT", `Intro: ${introResponse.text.length} chars, resp_id=${lastResponseId?.slice(0, 25)}`);
  } else {
    introResponse = await draftProvider.call(introPrompt, {
      model: draftModel,
      systemPrompt,
      temperature: 0.6,
      reasoningEffort: "medium",
      step: "intro_generation", mapId, nodeId,
    });
  }
  totalCost += introResponse.costUsd || 0;

  const htmlParts: string[] = [];
  htmlParts.push(`<h1>${seoTitle}</h1>`);
  // Track effective intro response — retry path overrides original (BUG #3 fix)
  // so engine_debug.draft.intro persists the output that ACTUALLY landed in draft,
  // not the failed first attempt.
  let effectiveIntroResponse = introResponse;
  let introHtml = introResponse.text.trim();

  // Validate intro for meta artifacts — retry once with fallback if failed
  const introMetaCheck = validateNoMetaOutput(introHtml, "intro");
  if (!introMetaCheck.passed) {
    logPublic("DRAFT", `Intro meta artifacts found: ${introMetaCheck.found?.join(", ")}. Retrying with fallback...`);
    const retryResponse = useResponsesAPI && draftProvider.callResponses
      ? await draftProvider.callResponses(INTRO_FALLBACK, { model: draftModel, instructions: systemPrompt, reasoningEffort: "medium", step: "intro_generation", mapId, nodeId })
      : await draftProvider.call(INTRO_FALLBACK, { model: draftModel, systemPrompt, temperature: 0.6, step: "intro_generation", mapId, nodeId });
    effectiveIntroResponse = retryResponse;
    introHtml = retryResponse.text.trim();
    totalCost += retryResponse.costUsd || 0;
    lastResponseId = retryResponse.responseId || lastResponseId;
  }

  htmlParts.push(introHtml);

  // Validate intro doesn't contain H2/H3
  const introHasH2 = /<h[23][^>]*>/i.test(introHtml);
  logIntegrity("intro_no_headings", !introHasH2, introHasH2 ? { found: introHtml.match(/<h[23][^>]*>[^<]*<\/h[23]>/gi)?.slice(0, 3) } : undefined);
  logLlm("intro", { model: draftModel, provider: sectionPromptDb?.provider || stageConfig.provider, chaining: false }, { tokensInput: introResponse.tokensInput, tokensOutput: introResponse.tokensOutput, costUsd: introResponse.costUsd, responseLength: introHtml.length });

  // 5. Generate each block — Responses API with chaining OR Chat Completions with sliding window
  const previousBlockSummaries: string[] = []; // only used for Chat Completions fallback

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    onBlockProgress?.(i + 1, blocks.length);

    // Build KG data for this block
    const kgDataStr = [
      block.entities.length > 0 ? `Encje: ${block.entities.map((e) => `${e.entity_name} (${(e.definition || e.description || "").slice(0, 100)})`).join("; ")}` : "",
      block.facts.length > 0 ? `Fakty: ${block.facts.map((f) => `${f.fact}${f.category ? ` [${f.category}]` : ""}`).join("; ")}` : "",
      block.ideations.length > 0 ? `Pomysły: ${block.ideations.map((i) => i.description || i.ideation).join("; ")}` : "",
      block.relationships.length > 0 ? `Relacje: ${block.relationships.map((r) => `${(r.entity_id2text || [])[0] || "?"} ${r.predicate || "?"} ${(r.entity_id2text || [])[1] || "?"}`).join("; ")}` : "",
    ].filter(Boolean).join("\n");

    // Build H3 instructions — bez liczby słów (target_words usunięte 2026-04-26).
    // Charakter passage per H3 wynika z SRO Passage Tag w block.sourceArea (patrz passageGuidance niżej).
    const h3sFormatted = block.h3s.length > 0
      ? block.h3s.map((h3) => `- <h3>${h3.header}</h3> (format: ${h3.format})`).join("\n")
      : "(brak podsekcji — sekcja standalone)";

    const passageInstructions = getPassageInstructions(block.passageFormat);
    // Passage character per SRO Passage Tag (SRO 4.2). Zastapilo wczesniejszy SRO_TAG_TO_H3_LENGTH
    // (zakresy 80-150/150-280/200-380) ktore byly arbitralnym artefaktem, nie SRO. Teraz LLM
    // dostaje CHARAKTER passage (extractable claim / named entity / verifiable detail / format-frame),
    // dlugosc wynika z naturalnej tresci. Anti-symmetry egzekwowana przez validateH3Variance() guard.
    const passageGuidance = buildPassageGuidance(block.sourceArea);

    // Build EAV data for this block — 3-level fallback hierarchy (F1b):
    // (a) matchedEav — EAV dla entities przypisanych do sekcji (existing)
    // (b) semanticEav — jesli (a) pusty I source_area ma tag SRO → filtruj
    //     ctxEav po attribute_name matchujacym pattern z SRO_TAG_TO_EAV_PATTERNS
    // (c) roundRobinEav — jesli (a) i (b) puste, round-robin per block index
    const blockEntityNames = block.entities
      .map((e: any) => (e.entity_name || e.name || "").toLowerCase())
      .filter(Boolean);
    const matchedEav = (ctxEav || [])
      .filter((a) => blockEntityNames.includes(a.entity_name.toLowerCase()))
      .slice(0, 10);

    let semanticEav: typeof matchedEav = [];
    let eavSource: "matched" | "semantic" | "round-robin" | "empty" = "empty";
    if (matchedEav.length > 0) {
      eavSource = "matched";
    } else {
      const sroTag = extractSroTag(block.sourceArea);
      const pattern = sroTag ? SRO_TAG_TO_EAV_PATTERNS[sroTag] : null;
      if (pattern) {
        semanticEav = (ctxEav || [])
          .filter((a) => pattern.test(a.attribute_name))
          .slice(0, 10);
        if (semanticEav.length > 0) {
          eavSource = "semantic";
          logDebug(
            "Draft",
            `Section ${block.sectionOrder} [${sroTag}] fallback EAV semantic: ${semanticEav.length} matches (${semanticEav.map((a) => a.attribute_name).join(", ")})`,
          );
        }
      }
    }

    const roundRobinEav = (ctxEav || []).filter((_, idx) => idx % blocks.length === i);
    const primaryEav = matchedEav.length > 0 ? matchedEav : semanticEav;
    const finalEav = (primaryEav.length > 0 ? primaryEav : roundRobinEav).slice(0, 5);
    if (eavSource === "empty" && finalEav.length > 0) eavSource = "round-robin";
    if (eavSource !== "matched" && eavSource !== "empty") {
      logPublic(
        "Draft",
        `Section ${block.sectionOrder} EAV source=${eavSource} (${finalEav.length} attrs)`,
      );
    }
    const relevantEav = finalEav
      .map((a) => `${a.entity_name}.${a.attribute_name} = ${a.value}`)
      .join("\n");

    // Build block prompt from DB section_generation template OR fallback
    const BLOCK_FALLBACK = `Napisz sekcję artykułu w języku ${language}.\n\n## Nagłówek H2\n<h2>${block.h2Header}</h2>\n\n## Typ sekcji\n${block.sectionType === "full" ? "Pełna sekcja z podsekcjami" : "Sekcja kontekstowa (kompaktowa)"}\n\n## Podsekcje H3\n${h3sFormatted}\n\n## Format pisania\n${passageInstructions}\n\n## Dane z Knowledge Graph\n${kgDataStr || "(brak)"}${relevantEav ? "\n\n## Atrybuty encji (EAV — wpleć fakty naturalnie):\n" + relevantEav : ""}\n\n## Linki wewnętrzne:\n${internalLinksFormatted}\n\n## Charakter passage (per SRO Passage Tag, SRO 4.2)\n${passageGuidance}\n\n## Zasady\n- HTML semantyczny\n- NIE powtarzaj informacji z poprzednich sekcji\n- Zwróć TYLKO HTML\n- NIGDY nie wyswietlaj zmiennych szablonowych ani placeholderow`;

    const blockTemplateVars: Record<string, string> = {
      h2_header: block.h2Header,
      section_type_description: block.sectionType === "full" ? "Pełna sekcja z podsekcjami" : "Sekcja kontekstowa (kompaktowa)",
      h3s_formatted: h3sFormatted,
      passage_instructions: passageInstructions,
      kg_data_formatted: kgDataStr || "(brak przypisanych danych)",
      language,
      passage_guidance_per_sro_tag: passageGuidance,
      internal_links_formatted: internalLinksFormatted || "(brak)",
      eav_formatted: relevantEav || "(brak danych EAV dla tej sekcji)",
      // STOP-C': extra vars dla pillar (cluster_member_entities, cluster_member_slugs, macro_statement, outline)
      ...(draftOptions?.extraVars ?? {}),
      // Pillar variants moga uzyc current_h2_title — alias dla h2_header (kompatybilnosc z pillar prompt template)
      current_h2_title: block.h2Header,
    };

    let blockPrompt: string;
    if (sectionPromptDb) {
      try {
        blockPrompt = compilePrompt(sectionPromptDb.template, blockTemplateVars, "section_generation");
      } catch (compileErr) {
        logPublic("DRAFT", `Block ${i + 1}: compilePrompt failed, using fallback: ${compileErr instanceof Error ? compileErr.message : compileErr}`);
        blockPrompt = BLOCK_FALLBACK;
      }
    } else {
      blockPrompt = BLOCK_FALLBACK;
    }

    let blockResponse;
    // Snapshot lastResponseId PRZED first call — wykorzystamy go ponownie jesli anti-symmetry retry
    // (retry zastepuje first generation, wiec previousResponseId dla retry = lastResponseId z POPRZEDNIEGO bloku, nie blockResponse z first try)
    const prevBlockResponseId = lastResponseId;
    if (useResponsesAPI && draftProvider.callResponses) {
      // Responses API with chaining — model remembers previous blocks
      logPublic("DRAFT", `Blok ${i + 1}/${blocks.length}`);
      logDebug("DRAFT", `Blok ${i + 1}/${blocks.length}: model=${draftModel}, chaining=${!!prevBlockResponseId}, prev_id=${prevBlockResponseId?.slice(0, 25) || "none"}`);
      blockResponse = await draftProvider.callResponses(blockPrompt, {
        model: draftModel,
        instructions: systemPrompt,
        reasoningEffort: "medium",
        previousResponseId: prevBlockResponseId || undefined,
        step: "section_generation", mapId, nodeId,
      });
      logDebug("DRAFT", `Blok ${i + 1}: ${blockResponse.text.length} chars, resp_id=${blockResponse.responseId?.slice(0, 25)}`);
    } else {
      // Chat Completions fallback with sliding window
      const contextWindow = previousBlockSummaries.slice(-2).join("\n\n").slice(0, 2000);
      const blockPromptWithContext = contextWindow
        ? blockPrompt + `\n\n## Kontekst z poprzednich sekcji\n${contextWindow}`
        : blockPrompt;

      blockResponse = await draftProvider.call(blockPromptWithContext, {
        model: draftModel,
        systemPrompt,
        temperature: 0.6,
        reasoningEffort: "medium",
        step: "section_generation", mapId, nodeId,
      });
    }
    totalCost += blockResponse.costUsd || 0;

    let blockHtml = blockResponse.text.trim();

    // ============================================================
    // Anti-symmetry post-processing guard (Krok 3, 2026-04-26)
    // ============================================================
    // Skip jesli <2 H3. Sprawdz KAZDA pare sasiednich, retry max 1 jesli >=1 violation.
    let antiSymRetryTriggered = false;
    let beforeRetryWords: number[] | null = null;
    if (block.h3s.length >= 2) {
      const wordsPerH3 = countH3Words(blockHtml);
      // Skip jesli faktycznie mniej niz 2 H3 wykryte w HTML (LLM moze byl skrocil)
      if (wordsPerH3.length >= 2) {
        const variance = validateH3Variance(wordsPerH3);
        if (!variance.ok) {
          antiSymRetryTriggered = true;
          beforeRetryWords = wordsPerH3;
          const violationDesc = variance.violations
            .map((v) => `H3.${v.idx1 + 1}(${v.w1}slow) vs H3.${v.idx2 + 1}(${v.w2}slow) diff=${v.diff}`)
            .join("; ");
          logPublic("DRAFT", `Block ${i + 1} anti-symmetry violation, retry: ${violationDesc}`);

          const retrySuffix = `\n\nWARNING ANTI-SYMMETRY: poprzednia generacja miala H3 o zblizonej dlugosci (${violationDesc}, prog ${H3_VARIANCE_THRESHOLD} slow). Przeredaguj zachowujac SRO 4.2 kryteria, ale tak zeby naturalna tresc dawala roznice >=${H3_VARIANCE_THRESHOLD} slow miedzy sasiednimi H3. Skroc krotszy H3 do core claimu LUB rozszerz dluzszy H3 o naturalny element (przyklad/mechanism/edge case z EAV). NIE dodawaj fillera.`;
          const retryPrompt = blockPrompt + retrySuffix;

          let retryResponse;
          if (useResponsesAPI && draftProvider.callResponses) {
            retryResponse = await draftProvider.callResponses(retryPrompt, {
              model: draftModel,
              instructions: systemPrompt,
              reasoningEffort: "medium",
              previousResponseId: prevBlockResponseId || undefined,
              step: "section_generation", mapId, nodeId,
            });
          } else {
            const contextWindow = previousBlockSummaries.slice(-2).join("\n\n").slice(0, 2000);
            const retryPromptWithContext = contextWindow
              ? retryPrompt + `\n\n## Kontekst z poprzednich sekcji\n${contextWindow}`
              : retryPrompt;
            retryResponse = await draftProvider.call(retryPromptWithContext, {
              model: draftModel,
              systemPrompt,
              temperature: 0.6,
              reasoningEffort: "medium",
              step: "section_generation", mapId, nodeId,
            });
          }
          totalCost += retryResponse.costUsd || 0;
          blockResponse = retryResponse; // override for downstream chaining + perBlockDebug
          blockHtml = retryResponse.text.trim();
        }
      }
    }

    // Update lastResponseId AFTER retry decision (chaining bazuje na finalnym blockResponse)
    if (useResponsesAPI && draftProvider.callResponses) {
      lastResponseId = blockResponse.responseId || null;
    }

    // Anti-symmetry integrity log (audit trail per block z H3)
    if (block.h3s.length >= 2) {
      const finalWords = countH3Words(blockHtml);
      const finalVariance = validateH3Variance(finalWords);
      if (antiSymRetryTriggered) {
        const beforeViolations = beforeRetryWords ? validateH3Variance(beforeRetryWords).violations.length : 0;
        logIntegrity("anti_symmetry_retry_triggered", finalVariance.ok, {
          block: i + 1,
          h2: block.h2Header,
          h3_count_outline: block.h3s.length,
          before_words: beforeRetryWords,
          after_words: finalWords,
          before_violations: beforeViolations,
          after_violations: finalVariance.violations.length,
          retry_succeeded: finalVariance.ok,
          threshold: H3_VARIANCE_THRESHOLD,
        });
        if (!finalVariance.ok) {
          logPublic("DRAFT", `Block ${i + 1} anti-symmetry persists po retry — accepting (max 1 retry)`);
        }
      } else {
        logIntegrity("anti_symmetry_check", finalVariance.ok, {
          block: i + 1,
          h2: block.h2Header,
          h3_count: finalWords.length,
          words_per_h3: finalWords,
          violations: finalVariance.violations.length,
          threshold: H3_VARIANCE_THRESHOLD,
        });
      }
    }

    // Validate no meta/template artifacts in output
    const metaCheck = validateNoMetaOutput(blockHtml, `block_${i + 1}`);
    if (!metaCheck.passed) {
      logPublic("DRAFT", `Block ${i + 1} "${block.h2Header}": meta artifacts found: ${metaCheck.found?.join(", ")}`);
    }

    htmlParts.push(blockHtml);

    // Per-block validation: does HTML contain the expected H2?
    const expectedH2Norm = block.h2Header.toLowerCase().slice(0, 30);
    const blockHasExpectedH2 = blockHtml.toLowerCase().includes(expectedH2Norm) || /<h2[^>]*>/i.test(blockHtml);
    logIntegrity(`block_${i + 1}_h2_present`, blockHasExpectedH2, { expected: block.h2Header, block_chars: blockHtml.length, has_h2_tag: /<h2[^>]*>/i.test(blockHtml) });
    logLlm(`draft_block_${i + 1}`, { model: draftModel, provider: sectionPromptDb?.provider || stageConfig.provider, chaining: useResponsesAPI && !!lastResponseId }, { tokensInput: blockResponse.tokensInput, tokensOutput: blockResponse.tokensOutput, costUsd: blockResponse.costUsd, durationMs: 0, responseLength: blockHtml.length });

    perBlockDebug.push({
      h2: block.h2Header,
      chars: blockHtml.length,
      format: block.passageFormat,
      cost_usd: blockResponse.costUsd || 0,
      prompt: blockPrompt,
      system_prompt: systemPrompt,
      h3s: block.h3s.map((h) => h.header),
      kg_entities: block.entities.map((e) => e.entity_name),
      kg_facts: block.facts.map((f) => f.fact),
      chaining: useResponsesAPI && !!lastResponseId,
      response_id: blockResponse.responseId,
    });

    // Sliding window summary — only for Chat Completions fallback
    if (!useResponsesAPI) {
      const lastParagraph = blockHtml.replace(/<[^>]+>/g, "").split("\n").filter(Boolean).pop();
      if (lastParagraph) previousBlockSummaries.push(lastParagraph.slice(0, 500));
    }
  }

  // 5. Assemble final HTML
  const draftHtml = htmlParts.join("\n\n");
  const totalWords = draftHtml.replace(/<[^>]+>/g, " ").split(/\s+/).length;
  logStage("DRAFT", "END", { total_blocks: blocks.length, total_chars: draftHtml.length, total_words: totalWords, total_cost: totalCost });

  // 6. Save to DB
  await (supabase as any)
    .from("thematic_node_content")
    .update({
      draft_html: draftHtml,
      engine_stage: "draft",
      content_generated_at: new Date().toISOString(),
    })
    .eq("node_id", nodeId);

  const debug = {
    blocks_generated: blocks.length,
    per_block: perBlockDebug,
    total_words: draftHtml.replace(/<[^>]+>/g, " ").split(/\s+/).length,
    total_cost_usd: totalCost,
    // KB 4.1 forensics — analogicznie outline d77b950. Path: engine_debug.draft.intro.*
    // BUG #3 fix: response_text persists raw intro output BEFORE optimization+humanization
    // so iterative intro prompt testing has access to the actual LLM generation.
    // effectiveIntroResponse reflects retry path (if triggered) — it's the response
    // whose text ACTUALLY landed in draftHtml.
    intro: {
      prompt: introPrompt,
      hook_type: introHookType,
      hook_subpattern: introHookSubpattern,
      response_text: effectiveIntroResponse?.text || "",
      response_chars: effectiveIntroResponse?.text?.length || 0,
      cost_usd: effectiveIntroResponse?.costUsd || 0,
    },
  };

  return { draftHtml, costUsd: totalCost, debug };
}
